import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Activity, Mail, Lock, Loader2, Eye, EyeOff } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import { useAuthStore } from "@/shared/store/authStore"
import { useToast } from "@/shared/hooks/useToast"

// 区域码选项
const REGION_CODES = [
  { code: "0086", name: "中国", flag: "🇨🇳" },
  { code: "0001", name: "美国", flag: "🇺🇸" },
  { code: "0044", name: "英国", flag: "🇬🇧" },
  { code: "0081", name: "日本", flag: "🇯🇵" },
  { code: "0082", name: "韩国", flag: "🇰🇷" },
  { code: "00852", name: "香港", flag: "🇭🇰" },
  { code: "00853", name: "澳门", flag: "🇲🇴" },
  { code: "00886", name: "台湾", flag: "🇹🇼" },
  { code: "0065", name: "新加坡", flag: "🇸🇬" },
]

/**
 * 登录页面
 * 符合苹果设计规范：简洁、优雅、大圆角、充足留白
 */
export default function Login() {
  const navigate = useNavigate()
  const { login, isLoading, error, clearError } = useAuthStore()
  // v3.0功能：验证码登录相关（暂时保留）
  // const { loginWithCode } = useAuthStore()
  const { showError, showSuccess } = useToast()

  // v3.0功能：验证码登录相关状态（暂时保留）
  // const [loginType, setLoginType] = useState<"password" | "code">("password")
  const [formData, setFormData] = useState({
    regionCode: "0086",
    email: "",
    password: "",
    // v3.0功能：验证码登录相关状态（暂时保留）
    // code: "",
  })
  const [localError, setLocalError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  // v3.0功能：验证码登录相关状态（暂时保留）
  // const [countdown, setCountdown] = useState(0)
  // const [isSendingCode, setIsSendingCode] = useState(false)

  // v3.0功能：验证码登录相关函数（暂时保留）
  // useEffect(() => {
  //   if (countdown > 0) {
  //     const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
  //     return () => clearTimeout(timer)
  //   }
  // }, [countdown])

  // const handleSendCode = async () => {
  //   if (!formData.email.trim()) {
  //     setLocalError("请先输入邮箱地址")
  //     return
  //   }
  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  //   if (!emailRegex.test(formData.email)) {
  //     setLocalError("请输入有效的邮箱地址")
  //     return
  //   }
  //   setIsSendingCode(true)
  //   setLocalError(null)
  //   try {
  //     await AuthApiService.sendVerificationCode({ email: formData.email.trim() })
  //     showSuccess("验证码已发送，请查收邮件")
  //     setCountdown(60)
  //   } catch (err: unknown) {
  //     const errorMessage = getErrorMessage(
  //       err && typeof err === 'object' && 'response' in err
  //         ? (err as { response?: { data?: { error?: { message?: string; code?: string } } } }).response?.data?.error
  //         : undefined
  //     ) || '发送验证码失败，请稍后重试'
  //     setLocalError(errorMessage)
  //     showError(errorMessage)
  //   } finally {
  //     setIsSendingCode(false)
  //   }
  // }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('🔐 [Login] 表单提交开始')
    setLocalError(null)
    clearError()

    // 基础验证
    if (!formData.email.trim()) {
      console.log('❌ [Login] 邮箱为空')
      setLocalError("请输入邮箱地址")
      return
    }

    if (!formData.password.trim()) {
      console.log('❌ [Login] 密码为空')
      setLocalError("请输入密码")
      return
    }

    // 邮箱格式验证
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      console.log('❌ [Login] 邮箱格式无效')
      setLocalError("请输入有效的邮箱地址")
      return
    }

    console.log('✅ [Login] 表单验证通过，开始登录请求')
    
    try {
      console.log('📤 [Login] 调用 login 函数:', { 
        region_code: formData.regionCode,
        email: formData.email.trim() 
      })
      const result = await login({
        region_code: formData.regionCode,
        email: formData.email.trim(),
        password: formData.password,
      })
      console.log('📥 [Login] login 函数返回结果:', result)
      
      if (!result) {
        console.error('❌ [Login] login 返回 false')
        throw new Error("登录失败，请检查邮箱和密码")
      }
      
      console.log('✅ [Login] 登录成功，准备跳转')
      showSuccess("登录成功")
      navigate("/")
    } catch (err) {
      console.error('❌ [Login] 登录异常:', err)
      const errorMessage =
        err instanceof Error ? err.message : "登录失败，请稍后重试"
      setLocalError(errorMessage)
      showError(errorMessage)
    }
  }

  const displayError = localError || error

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo和标题 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-4">
            <Activity className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">欢迎回来</h1>
          <p className="text-slate-500 dark:text-slate-400">
            登录以继续使用 Axiom
          </p>
        </div>

        {/* 登录卡片 */}
        <Card className="border-0 shadow-xl backdrop-blur-sm bg-white/80 dark:bg-slate-900/80">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-semibold">登录</CardTitle>
            <CardDescription>
              使用您的邮箱和密码登录账户
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* v3.0功能：验证码登录已实现但暂时隐藏 */}
            {/* <Tabs value={loginType} onValueChange={(v) => {
              setLoginType(v as "password" | "code")
              setLocalError(null)
              setFormData({ ...formData, password: "", code: "" })
            }} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="password">密码登录</TabsTrigger>
                <TabsTrigger value="code">验证码登录</TabsTrigger>
              </TabsList> */}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* 区域码 + 邮箱输入 */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    邮箱地址
                  </Label>
                  <div className="flex gap-2">
                    {/* 区域码选择 */}
                    <Select
                      value={formData.regionCode}
                      onValueChange={(value) => setFormData({ ...formData, regionCode: value })}
                      disabled={isLoading}
                    >
                      <SelectTrigger className="w-[130px] h-11 rounded-lg border-slate-200 dark:border-slate-800">
                        <SelectValue>
                          {REGION_CODES.find(r => r.code === formData.regionCode)?.flag}{" "}
                          {formData.regionCode}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {REGION_CODES.map((region) => (
                          <SelectItem key={region.code} value={region.code}>
                            <span className="flex items-center gap-2">
                              <span>{region.flag}</span>
                              <span>{region.code}</span>
                              <span className="text-slate-500 text-xs">({region.name})</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* 邮箱输入 */}
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value })
                          setLocalError(null)
                        }}
                        className="pl-10 h-11 rounded-lg border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-2 focus:ring-primary/20"
                        disabled={isLoading}
                        autoComplete="email"
                        autoFocus
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>
                </div>

                {/* 密码输入 */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    密码
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => {
                        setFormData({ ...formData, password: e.target.value })
                        setLocalError(null)
                      }}
                      className="pl-10 pr-10 h-11 rounded-lg border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-2 focus:ring-primary/20"
                      disabled={isLoading}
                      autoComplete="current-password"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck="false"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* v3.0功能：验证码登录已实现但暂时隐藏 */}
                {/* <TabsContent value="code" className="space-y-5 mt-0">
                  <div className="space-y-2">
                    <Label htmlFor="code" className="text-sm font-medium">
                      验证码
                    </Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="code"
                          type="text"
                          placeholder="6位验证码"
                          value={formData.code}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                            setFormData({ ...formData, code: value })
                            setLocalError(null)
                          }}
                          className="pl-10 h-11 rounded-lg border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-2 focus:ring-primary/20 text-center text-lg tracking-widest"
                          disabled={isLoading}
                          maxLength={6}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleSendCode}
                        disabled={isSendingCode || countdown > 0 || isLoading || !formData.email.trim()}
                        className="h-11 px-4 whitespace-nowrap"
                      >
                        {isSendingCode ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : countdown > 0 ? (
                          `${countdown}秒`
                        ) : (
                          '发送验证码'
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">
                      验证码将发送到您的邮箱，有效期5分钟
                    </p>
                  </div>
                </TabsContent> */}

              {/* 错误提示 */}
              {displayError && (
                <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3">
                  <p className="text-sm text-destructive">{displayError}</p>
                </div>
              )}

              {/* 忘记密码链接 */}
              <div className="flex items-center justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  忘记密码？
                </Link>
              </div>

              {/* 提交按钮 */}
              <Button
                type="submit"
                className="w-full h-11 rounded-lg text-base font-medium shadow-sm hover:shadow-md transition-all"
                disabled={isLoading}
                data-testid="login-submit-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    登录中...
                  </>
                ) : (
                  "登录"
                )}
              </Button>
            </form>
            {/* </Tabs> - v3.0功能：验证码登录已实现但暂时隐藏 */}

            {/* 注册链接 */}
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                还没有账户？{" "}
                <Link
                  to="/register"
                  className="text-primary font-medium hover:underline"
                >
                  立即注册
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 底部说明 */}
        <p className="mt-6 text-center text-xs text-slate-400 dark:text-slate-500">
          登录即表示您同意我们的服务条款和隐私政策
        </p>
      </div>
    </div>
  )
}

